import { useState, useEffect, useCallback } from "react";
import API, { formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, Loader2, Image, Eye, EyeOff, GripVertical } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const emptyBanner = { title: "", subtitle: "", image_url: "", cta_text: "", cta_link: "", is_active: true, sort_order: 0 };

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyBanner);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/banners");
      setBanners(data || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(emptyBanner); setEditing(null); setError(""); setShowForm(true); };
  const openEdit = (b) => { setForm({ ...b }); setEditing(b.id); setError(""); setShowForm(true); };

  const handleSave = async () => {
    if (!form.title.trim()) { setError("Title is required"); return; }
    setSaving(true); setError("");
    try {
      if (editing) {
        await API.put(`/admin/banners/${editing}`, form);
      } else {
        await API.post("/admin/banners", form);
      }
      setShowForm(false);
      load();
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    try {
      await API.delete(`/admin/banners/${id}`);
      load();
    } catch {}
  };

  const toggleActive = async (banner) => {
    try {
      await API.put(`/admin/banners/${banner.id}`, { ...banner, is_active: !banner.is_active });
      load();
    } catch {}
  };

  return (
    <div className="space-y-6" data-testid="admin-banners">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Banners</h2>
          <p className="text-sm text-gray-500 mt-0.5">Manage promotional banners shown on the customer dashboard</p>
        </div>
        <Button onClick={openCreate} className="bg-[#008A99] hover:bg-[#006670] text-white rounded-xl shadow-lg shadow-[#008A99]/20" data-testid="add-banner-button">
          <Plus className="w-4 h-4 mr-2" /> Add Banner
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#008A99]" /></div>
      ) : banners.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center shadow-sm">
          <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Image className="w-7 h-7 text-gray-300" />
          </div>
          <p className="text-gray-500 font-medium">No banners yet</p>
          <p className="text-sm text-gray-400 mt-1">Create promotional banners to engage customers</p>
        </div>
      ) : (
        <div className="space-y-3">
          {banners.map((banner) => (
            <div key={banner.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-all duration-200" data-testid={`banner-item-${banner.id}`}>
              <div className="flex items-center">
                {/* Preview */}
                <div className="w-40 h-24 bg-gray-100 flex-shrink-0 relative overflow-hidden">
                  {banner.image_url ? (
                    <img src={banner.image_url} alt={banner.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-8 h-8 text-gray-300" />
                    </div>
                  )}
                  {!banner.is_active && (
                    <div className="absolute inset-0 bg-gray-900/50 flex items-center justify-center">
                      <span className="text-xs text-white font-bold bg-gray-800 px-2 py-0.5 rounded">Hidden</span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 px-5 py-3">
                  <h3 className="font-bold text-gray-900 text-sm" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>{banner.title}</h3>
                  {banner.subtitle && <p className="text-xs text-gray-500 mt-0.5">{banner.subtitle}</p>}
                  {banner.cta_text && <span className="inline-block mt-1.5 text-[10px] bg-[#E0F4F5] text-[#008A99] font-semibold px-2 py-0.5 rounded-full">{banner.cta_text}</span>}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 px-4">
                  <button onClick={() => toggleActive(banner)} className={`p-2 rounded-lg transition-colors ${banner.is_active ? 'text-emerald-500 hover:bg-emerald-50' : 'text-gray-400 hover:bg-gray-100'}`} data-testid={`toggle-${banner.id}`}>
                    {banner.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button onClick={() => openEdit(banner)} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors" data-testid={`edit-banner-${banner.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(banner.id)} className="p-2 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors" data-testid={`delete-banner-${banner.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Banner Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>{editing ? "Edit Banner" : "Create Banner"}</DialogTitle></DialogHeader>
          <div className="bg-[#E0F4F5] border border-[#008A99]/20 rounded-lg p-3 text-xs text-[#005a66] flex gap-2 items-start" data-testid="banner-dimension-hint">
            <Image className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Recommended banner dimensions</p>
              <ul className="mt-1 space-y-0.5 text-[11px] leading-snug">
                <li>• <b>Home carousel:</b> 1200 × 480 px (5:2 ratio) — JPG/PNG/WEBP</li>
                <li>• <b>Mobile-safe area:</b> Keep text/logos inside the centre 1000 × 400 px</li>
                <li>• <b>Max file size:</b> 500 KB for fastest loading</li>
              </ul>
            </div>
          </div>
          {error && <div className="p-2.5 bg-red-50 text-red-600 text-sm rounded-lg font-medium">{error}</div>}
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Title *</label>
              <Input data-testid="banner-title-input" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. 2x Points Weekend" className="h-10 rounded-xl" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Subtitle</label>
              <Input data-testid="banner-subtitle-input" value={form.subtitle} onChange={(e) => setForm(f => ({ ...f, subtitle: e.target.value }))} placeholder="e.g. Limited Time Offer" className="h-10 rounded-xl" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Image URL</label>
              <Input data-testid="banner-image-input" value={form.image_url} onChange={(e) => setForm(f => ({ ...f, image_url: e.target.value }))} placeholder="https://..." className="h-10 rounded-xl" />
              {form.image_url && (
                <div className="mt-2 h-24 rounded-lg overflow-hidden bg-gray-100">
                  <img src={form.image_url} alt="Preview" className="w-full h-full object-cover" onError={(e) => e.target.style.display='none'} />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">CTA Text</label>
                <Input data-testid="banner-cta-text" value={form.cta_text} onChange={(e) => setForm(f => ({ ...f, cta_text: e.target.value }))} placeholder="Learn More" className="h-10 rounded-xl" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">CTA Link</label>
                <Input data-testid="banner-cta-link" value={form.cta_link} onChange={(e) => setForm(f => ({ ...f, cta_link: e.target.value }))} placeholder="/app/products" className="h-10 rounded-xl" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Sort Order</label>
              <Input data-testid="banner-sort-order" type="number" value={form.sort_order} onChange={(e) => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} className="h-10 rounded-xl w-24" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#008A99] hover:bg-[#006670] text-white rounded-xl" data-testid="save-banner-button">
              {saving ? "Saving..." : "Save Banner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
